import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSettings } from "@/context/SettingsContext";
import { useBooking } from "@/context/BookingContext";

const links = [
  { label: "Home", to: "/" },
  { label: "Models", to: "/#models" },
  { label: "Pillars", to: "/#pillars" },
  { label: "Team", to: "/#team" },
  { label: "Journal", to: "/blog" },
  { label: "Subscribe", to: "/#newsletter" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    if (window.__lenis) window.__lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const handleNav = (to) => {
    setOpen(false);
    if (to === "/") {
      if (location.pathname !== "/") navigate("/");
      else scrollToTop();
    } else if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToId(id), 400);
      } else {
        scrollToId(id);
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background/90 backdrop-blur-md border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => handleNav("/")}
          className="font-display font-extrabold tracking-tighter text-lg sm:text-xl uppercase leading-none text-left"
        >
          Design My<br />
          <span className="text-primary">Cashflow</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => handleNav(l.to)}
              className="font-mono-accent text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={openBooking}
            className="bg-primary text-primary-foreground font-display font-bold text-sm uppercase tracking-wide px-5 py-2.5 hover:bg-[#1AE39C] transition-colors"
          >
            {settings.cta_label || "Design My Cashflow Now"}
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-foreground p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-background border-b border-border"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.label}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => handleNav(l.to)}
                  className="font-display text-2xl font-bold uppercase tracking-tight text-left hover:text-primary transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta"
                onClick={() => { setOpen(false); openBooking(); }}
                className="mt-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-wide px-5 py-3 text-left"
              >
                {settings.cta_label || "Design My Cashflow Now"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
